import { useSelector } from 'react-redux';
import { DateTimeUtcService, useAppNowUtc, useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { Stack } from '@optimus-web/core';
import { Reservation, ReservationTimeState } from '@mews-commander/data-access';
import { ApplicationState } from '@mews-commander/types';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { isAnyClientEnterpriseIntegrationsEnabled } from '@mews-commander/legacy-entities/src/ducks/enterpriseIntegrations';
import { UPSELL_HOUR_AVAILABILITY } from '../../constants';
import { getOakyUpsellingClientId } from './selectors';
import { ReservationOrderItems } from './ReservationOrderItems';
import { Balances } from './Balances';
import ReservationDetailCard from './ReservationDetailCard';
import { BasicReservationInfoCard } from './BasicReservationInfoCard';
import Upsell from './Upsell';

interface Props {
    reservation: Reservation;
}

const ReservationInfo = ({ reservation }: Props) => {
    const { id, groupId, reservationTimeState, serviceId, startUtc } = reservation;

    const isUpsellingEnabled = useFeatureFlag(FeatureFlag.upsellEnabled);
    const enterpriseId = useCurrentEnterpriseId();
    const clientId = useSelector(getOakyUpsellingClientId);
    const isUpsellingIntegrationEnabled = useSelector((state: ApplicationState) =>
        isAnyClientEnterpriseIntegrationsEnabled(state, { enterpriseId, clientId })
    );

    const nowDateTimeUtc = useAppNowUtc();
    const upsellableWindow = DateTimeUtcService.diffHours(DateTimeUtcService.parse(startUtc), nowDateTimeUtc);
    const startsInUpsellableWindow = upsellableWindow <= UPSELL_HOUR_AVAILABILITY && upsellableWindow > 0;

    const isUpsellable =
        startsInUpsellableWindow ||
        reservationTimeState === ReservationTimeState.ToBeCheckedIn ||
        reservationTimeState === ReservationTimeState.CheckedIn ||
        reservationTimeState === ReservationTimeState.ToBeCheckedOut;

    return (
        <Stack vertical spacing="200">
            <BasicReservationInfoCard reservation={reservation} />

            {isUpsellingEnabled && isUpsellingIntegrationEnabled && isUpsellable ? <Upsell reservationId={id} /> : null}

            <Balances
                reservationId={id}
                serviceId={serviceId}
                groupId={groupId}
                reservationTimeState={reservationTimeState}
            />

            <ReservationOrderItems reservationId={id} serviceId={serviceId} />

            <ReservationDetailCard reservation={reservation} />
        </Stack>
    );
};

export default ReservationInfo;
